// src/index.ts
var DEFAULT_REDACT_KEYS = /* @__PURE__ */ new Set([
  "token",
  "id_token",
  "access_token",
  "authorization",
  "password",
  "secret",
  "api_key",
  "email_body",
  "html_code",
  "customer_email"
]);
var SENSITIVE_QUERY_PARAM_PATTERN = /([?&](?:token|id_token|access_token|authorization|password|secret|api_key|customer_email)=)[^&#]*/gi;
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function redactSensitiveString(value) {
  return value.replace(SENSITIVE_QUERY_PARAM_PATTERN, "$1[REDACTED]");
}
function redactSensitiveValue(value, redactKeys = DEFAULT_REDACT_KEYS, seen) {
  if (typeof value === "string") return redactSensitiveString(value);
  if (Array.isArray(value)) {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    const result = value.map(
      (item) => redactSensitiveValue(item, redactKeys, seen)
    );
    seen.delete(value);
    return result;
  }
  if (isPlainObject(value)) {
    return redactSensitiveObject(value, redactKeys, seen);
  }
  return value;
}
function redactSensitiveObject(obj, redactKeys, seen) {
  if (seen.has(obj)) return "[Circular]";
  seen.add(obj);
  const result = {};
  for (const key of Object.keys(obj)) {
    if (redactKeys.has(key.toLowerCase())) {
      result[key] = "[REDACTED]";
    } else {
      result[key] = redactSensitiveValue(obj[key], redactKeys, seen);
    }
  }
  seen.delete(obj);
  return result;
}
function redactSensitiveProperties(obj, redactKeys = DEFAULT_REDACT_KEYS) {
  const redacted = redactSensitiveObject(obj, redactKeys, /* @__PURE__ */ new WeakSet());
  return redacted === "[Circular]" ? {} : redacted;
}
function normalizeError(error) {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  const normalised = new Error(String(error));
  normalised.name = "NonErrorException";
  return normalised;
}
function buildExceptionList(error) {
  return [
    {
      type: error.name || "Error",
      value: redactSensitiveString(error.message || String(error)),
      mechanism: {
        handled: true,
        synthetic: false
      },
      stacktrace: {
        type: "raw",
        frames: []
      }
    }
  ];
}
function sendExceptionInstantly(client, properties, uuid) {
  if (typeof client.capture !== "function") return;
  client.capture("$exception", properties, {
    _noTruncate: true,
    _batchKey: "exceptionEvent",
    send_instantly: true,
    ...uuid ? { uuid } : {}
  });
}
function createPostHogErrorTracking(client, config = {}) {
  const { appName, environment, release, enabled = true } = config;
  function buildMetadata(extra) {
    const metadata = {};
    if (appName) metadata.app_name = appName;
    if (environment) metadata.environment = environment;
    if (release) metadata.release = release;
    return { ...metadata, ...extra };
  }
  return {
    /**
     * Safely capture an exception and send it to PostHog.
     * - Normalises non-Error thrown values into an Error.
     * - Attaches app metadata.
     * - No-ops when disabled or when `captureException` is unavailable.
     * - Never throws.
     */
    captureException(error, properties) {
      if (!enabled) return;
      try {
        const normalisedError = normalizeError(error);
        const {
          feature,
          severity = "error",
          source,
          ...extraProperties
        } = properties ?? {};
        const merged = redactSensitiveProperties(
          buildMetadata({
            ...extraProperties,
            feature,
            severity,
            source
          })
        );
        for (const key of Object.keys(merged)) {
          if (merged[key] === void 0) delete merged[key];
        }
        if (typeof client.captureException === "function") {
          client.captureException(normalisedError, merged);
          return;
        }
        sendExceptionInstantly(
          client,
          {
            ...merged,
            $exception_level: merged.severity ?? "error",
            $exception_type: normalisedError.name || "Error",
            $exception_message: redactSensitiveString(normalisedError.message),
            $exception_list: buildExceptionList(normalisedError)
          }
        );
      } catch {
      }
    },
    /**
     * Attach a lightweight breadcrumb step to the current exception buffer.
     * Requires posthog-js >= 1.370.0 — no-ops safely on older versions.
     * Never throws.
     */
    addErrorContext(name, properties) {
      if (!enabled) return;
      try {
        if (typeof client.addExceptionStep !== "function") return;
        const safeProps = properties ? redactSensitiveProperties(properties) : void 0;
        client.addExceptionStep(name, safeProps);
      } catch {
      }
    }
  };
}
var NOISY_ERROR_PATTERNS = [
  /ResizeObserver loop/i,
  /Script error\.?$/i,
  /Loading chunk \d+ failed/i,
  /Network request failed/i,
  /Failed to fetch/i,
  /cancelled/i
];
function createBeforeSend(options = {}) {
  const {
    appName,
    environment,
    release,
    extraRedactKeys,
    extraNoisePatterns,
    filter
  } = options;
  const redactKeys = new Set(DEFAULT_REDACT_KEYS);
  if (extraRedactKeys) {
    for (const k of extraRedactKeys) redactKeys.add(k.toLowerCase());
  }
  const noisyErrorPatterns = (extraNoisePatterns == null ? void 0 : extraNoisePatterns.length) ? [...NOISY_ERROR_PATTERNS, ...extraNoisePatterns] : NOISY_ERROR_PATTERNS;
  return (event) => {
    if (!event) return event;
    const cloned = {
      ...event,
      properties: event.properties ? { ...event.properties } : void 0
    };
    const props = cloned.properties;
    if (!props) return cloned;
    if (event.event === "$exception") {
      const exceptionList = props["$exception_list"] ?? [];
      const firstException = exceptionList[0];
      if (firstException) {
        const msg = String(firstException.value ?? "");
        for (const pattern of noisyErrorPatterns) {
          if (pattern.test(msg)) return null;
        }
      }
    }
    const posthogRoutingToken = typeof props.token === "string" ? props.token : void 0;
    const redacted = redactSensitiveProperties(props, redactKeys);
    if (posthogRoutingToken) {
      redacted.token = posthogRoutingToken;
    }
    cloned.properties = redacted;
    if (appName) redacted.app_name = appName;
    if (environment) redacted.environment = environment;
    if (release) redacted.release = release;
    if (filter) {
      return filter(cloned);
    }
    return cloned;
  };
}
export {
  createBeforeSend,
  createPostHogErrorTracking,
  redactSensitiveProperties
};
